import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/jobs", label: "Jobs" },
  { to: "/employers", label: "Employers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 w-full transition-colors ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-border" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="container-tight flex items-center justify-between h-[88px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/cretre-logo.jpg" alt="CRETRE" className="h-16 lg:h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-navy-800" : "text-muted-foreground hover:text-navy-800"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/employers" className="hidden md:inline-flex">
            <Button
              data-testid="nav-hire-cta"
              className="bg-navy-800 hover:bg-navy-700 text-white rounded-full px-5 h-10 btn-lift"
            >
              Hire With Us
            </Button>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-border text-navy-800"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-border bg-white">
          <div className="container-tight py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-md text-base ${
                    isActive ? "bg-navy-50 text-navy-800 font-semibold" : "text-navy-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/employers" className="mt-2">
              <Button
                data-testid="nav-mobile-hire-cta"
                className="w-full bg-navy-800 hover:bg-navy-700 text-white rounded-full h-11"
              >
                Hire With Us
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
